import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useEffect, useRef, useState } from 'react';
import { Day, DayPicker, DayProps, IconLeft, IconRight, useNavigation } from 'react-day-picker';
import { Controller, ControllerProps, FieldPath, FieldValues } from 'react-hook-form';

import { ArrowRight, Calendar } from '../../assets/icons/Icons';
import isMobile from '../../helpers/isMobile';
import useClickOutside from '../../hooks/useClickOutside/useClickOutside';
import Button from '../Button/Button';
import Styles from './datepicker.module.scss';
import Tooltip from '../Tooltip/Tooltip';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const cx = classNames.bind(Styles);

const DEFAULT_START_PLACEHOLDER = 'Start';
const DEFAULT_END_PLACEHOLDER = 'End';
const DEFAULT_PLACEHOLDER = 'Date';

const CLASS_NAMES = {
  day: cx('day'),
  caption_label: cx('caption'),
  cell: cx('cell'),
  button: cx('button'),
  button_reset: cx('button-reset'),
  row: cx('row'),
  nav_button: cx('arrow'),
  head_cell: cx('head-cell'),
  table: cx('table'),
};

const MODIFIER_CLASS_NAMES = {
  selected: cx('day-selected') + ' selected',
  disabled: cx('day-disabled'),
  range_start: cx('day-selected'),
  range_end: cx('day-selected'),
  range_middle: cx('day-selected'),
  inBetween: cx('day-between'),
  today: cx('day-today'),
  outside: cx('day-outside'),
};

// TODO: the onchange will be different based on the useRange prop

interface Props {
  /** ID prop associated to Select and Label components for screen-readers to associate the two. **/
  id: string;
  /** Base function for onChange callback. **/
  onChange: (date: [Date, Date | null]) => void;
  /** Dictates whether or not dropdown is disabled. **/
  disabled?: boolean;
  /** Date Picker label text. **/
  label?: string;
  /** Minimum value for the date **/
  minDate?: Date;
  /** Maximum value for the date **/
  maxDate?: Date;
  /** Locale for the user which affects date formatting **/
  locale?: string;
  /** Dates that should be disabled in the calendar **/
  disabledDays?: Array<Date>;
  /** Should there be a range or single date chosen **/
  useRange: true;
  /** Current value for date picker */
  value?: [Date, Date | null] | Date[];
  /** modifier days to highlight */
  modifiers?: any;
  /** modifier days to highlight class names */
  modifiersClassNames?: any;
  /** modifier to disables some days */
  disabledDaysChecker?: (dt: Date) => boolean;
  position?: 'top' | 'bottom';
  fullWidth?: boolean;
  width?: string;
  showYear?: boolean;
  showTodayButton?: boolean;
  getDateTooltip?: (dt: Date) => string | null
}

interface PropsAlt {
  /** ID prop associated to Select and Label components for screen-readers to associate the two. **/
  id: string;
  /** Base function for onChange callback. **/
  onChange: (date: Date) => void;
  /** Dictates whether or not dropdown is disabled. **/
  disabled?: boolean;
  /** Date Picker label text. **/
  label?: string;
  /** Minimum value for the date **/
  minDate?: Date;
  /** Maximum value for the date **/
  maxDate?: Date;
  /** Locale for the user which affects date formatting **/
  locale?: string;
  /** Dates that should be disabled in the calendar **/
  disabledDays?: Array<Date>;
  /** Should there be a range or single date chosen **/
  useRange?: false;
  /** Current value for date picker */
  value?: Date;
  /** modifier days to highlight */
  modifiers?: any;
  /** modifier days to highlight class names */
  modifiersClassNames?: any;
  /** modifier to disables some days */
  disabledDaysChecker?: (dt: Date) => boolean;
  position?: 'top' | 'bottom';
  fullWidth?: boolean;
  width?: string;
  showYear?: boolean;
  showTodayButton?: boolean;
  getDateTooltip?: (dt: Date) => string | null
}

const DatePickerV2 = ({
  id,
  label,
  onChange,
  minDate,
  maxDate,
  disabled,
  locale,
  disabledDays,
  useRange,
  value,
  disabledDaysChecker,
  position = 'bottom',
  fullWidth = false,
  width,
  showYear = false,
  showTodayButton = false,
  getDateTooltip
}: Props | PropsAlt) => {
  const selectedDateRange = Array.isArray(value)
    ? {
        from: (value && value[0]) || null,
        to: (value && value[1]) || null,
      }
    : { from: value, to: value };
  const selectedDateSingle = Array.isArray(value) ? null : value || null;

  const [isOpen, setIsOpen] = useState(false);
  const [firstDaySelected, setFirstDaySelected] = useState(false);
  const [dayUnderMouse, setDayUnderMouse] = useState(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const mobile = isMobile();
  const calendarRef = useClickOutside<HTMLDivElement>(() => setIsOpen(false), isOpen);

  useEffect(() => {
    /* sets the calendar position */
    const setCalendarPos = () => {
      if (isOpen && buttonRef.current && calendarRef.current) {
        const {
          width: buttonWidth,
          left: buttonLeft,
          height: triggerHeight,
        } = buttonRef.current.getBoundingClientRect();
        const buttonCenterX = buttonLeft + buttonWidth / 2;

        const { right, width, bottom } = calendarRef.current.getBoundingClientRect();

        // Position needed to center calendar
        const calLeft = buttonCenterX - width / 2;
        const calRight = calLeft + width;

        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        const parentContainer = calendarRef.current.parentNode?.parentNode as HTMLDivElement;

        const { left: parentLeft } = parentContainer?.getBoundingClientRect() || { left: 0 };

        const style = calendarRef.current.style;

        if (calLeft <= 0) {
          // Left side is out of viewport
          style.left = 0 + 'px';
        } else if (calRight > viewportWidth) {
          // Right side is out of viewport
          const scrollbarWillAppear = bottom > (window.innerHeight || document.documentElement.clientHeight);
          const scrollbarWidth = scrollbarWillAppear ? 12 : 0;
          const overflow = right - viewportWidth + scrollbarWidth;

          style.left = -1 * overflow + 'px';
        } else {
          // Able to centre calendar
          style.left = calLeft - parentLeft + 'px';
        }

        if (position === 'top') {
          // 100% for calendar popup height, triggerHeight for button height, 1rem for margin
          style.transform = `translateY(calc(-100% - ${triggerHeight}px - 1rem))`;
        }
      }
    };

    setCalendarPos();
  }, [isOpen]);

  /**
   * Opens or closes calendar
   * @returns {void}
   */
  const handleCalendarVisible = () => {
    setIsOpen(!isOpen);
  };

  const onDatesChange = (day: Date) => {
    if (useRange) {
      const range = firstDaySelected
        ? {
            ...selectedDateRange,
            to: day,
          }
        : {
            from: day,
            to: null,
          };

      setFirstDaySelected(range.to ? false : true);

      if (range.to) {
        setIsOpen(false);
      }

      if (range.from && range.to) {
        // both picked sort properly
        if (dayjs(range.from).isSameOrBefore(range.to)) {
          onChange([range.from, range.to]);
        } else {
          onChange([range.to, range.from]);
        }
      } else {
        onChange([range.from || new Date(), range.to]);
      }
    } else {
      onChange(day);
      setIsOpen(false);
    }
  };

  const onDayHover = (day) => {
    if (selectedDateRange?.from && !selectedDateRange?.to) {
      setDayUnderMouse(day);
    } else {
      setDayUnderMouse(null);
    }
  };

  const inBewteenDaysMatcher = (d) => {
    if (!useRange) {
      return false;
    }
    if (selectedDateRange?.to || !selectedDateRange?.from) {
      return false;
    }

    const day = dayjs(d);
    const from = dayjs(selectedDateRange.from);

    return day.isAfter(from) && day.isSameOrBefore(dayUnderMouse);
  };

  const disabledMatcher = (d: Date): boolean => {
    const date = dayjs(d);

    return !!(
      (disabledDays &&
        disabledDays.map((day) => dayjs(day).format('DD-MM-YYYY')).includes(date.format('DD-MM-YYYY'))) ||
      (minDate && date.isBefore(dayjs(minDate))) ||
      (maxDate && date.isAfter(dayjs(maxDate))) ||
      (disabledDaysChecker && disabledDaysChecker(date.toDate()))
    );
  };

  /**
   * Creates human readable date from moment Date object
   * @param date date
   * @returns {void}
   */
  const formatDate = (date: Date | null | undefined) => {
    if (date) {
      return date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        year: showYear ? 'numeric' : undefined,
      });
    } else {
      return null;
    }
  };

  const wStyle = width ? { width, minWidth: width } : {};

  const datePickerButton = (
    <div style={wStyle} className={cx('form-element', 'datepicker-wrapper', { fullwidth: fullWidth, fw: fullWidth })}>
      {label && (
        <label htmlFor={id} className={cx('label', { 'label-disabled': disabled })}>
          {label}
        </label>
      )}
      <div className={cx('datepicker-container')}>
        <button
          className={cx('button-wrapper', {
            'calendar-wrapper-mobile': !useRange,
          })}
          onClick={handleCalendarVisible}
          disabled={disabled}
          ref={buttonRef}
          type='button'>
          <div className={cx('calendar')}>{Calendar}</div>
          <div className={cx('date-wrapper')}>
            {useRange ? (
              <>
                <div className={cx('date')} title={DEFAULT_START_PLACEHOLDER}>
                  <div>
                    {mobile && selectedDateRange?.from
                      ? dayjs(selectedDateRange.from).format(showYear ? 'MMM D YYYY' : 'MMM D')
                      : formatDate(selectedDateRange?.from) || DEFAULT_START_PLACEHOLDER}
                  </div>
                </div>
                <div className={cx('arrow-right')}>{ArrowRight}</div>
                <div className={cx('date')} title={DEFAULT_END_PLACEHOLDER}>
                  <div>
                    {mobile && selectedDateRange?.to
                      ? dayjs(selectedDateRange.to).format(showYear ? 'MMM D YYYY' : 'MMM D')
                      : formatDate(selectedDateRange?.to) || DEFAULT_END_PLACEHOLDER}
                  </div>
                </div>
              </>
            ) : (
              <div className={cx('date')} title={DEFAULT_PLACEHOLDER}>
                {mobile && selectedDateSingle
                  ? dayjs(selectedDateSingle).format(showYear ? 'MMM D YYYY' : 'MMM D')
                  : formatDate(selectedDateSingle) || DEFAULT_PLACEHOLDER}
              </div>
            )}
          </div>
        </button>
      </div>
    </div>
  );

  const footer = !showTodayButton ? (
    <></>
  ) : (
    <div className={`text-right ${Styles['footer']}`}>
      <Button
        size='small'
        type='primary'
        disabled={disabledMatcher(new Date())}
        onClick={() => onDatesChange(new Date())}>
        Today
      </Button>
    </div>
  );

  const DayCellWithTooltip = (props: DayProps,
) => {
  const tooltip = getDateTooltip ? getDateTooltip(props.date) : undefined
  if(tooltip){
  return (
  <Tooltip content={tooltip} position='top'>
        <Day {...props} />
  </Tooltip>
  )
  }
  return <Day {...props} />
}
  return (
    <>
      {datePickerButton}
      {isOpen && (
        <div className={cx('calendar-container')}>
          <div
            className={cx('calendar-wrapper', {
              'calendar-wrapper-mobile': mobile || !useRange,
            })}
            ref={calendarRef}>
            <DayPicker
              numberOfMonths={mobile || !useRange ? 1 : 2}
              fromDate={minDate}
              toDate={maxDate}
              defaultMonth={value && useRange ? value[0] : (value as Date) || new Date()}
              mode={useRange ? 'range' : undefined}
              onDayClick={onDatesChange}
              //@ts-ignore
              selected={selectedDateRange || undefined}
              modifiers={{ inBetween: inBewteenDaysMatcher }}
              disabled={disabledMatcher}
              modifiersClassNames={MODIFIER_CLASS_NAMES}
              classNames={CLASS_NAMES}
              onDayMouseEnter={onDayHover}
              components={mobile || !useRange ? { Caption: SingleMonthCaption } : {Day: DayCellWithTooltip}}
              showOutsideDays
              footer={footer}
            />
          </div>
        </div>
      )}
    </>
  );
};




export const SingleMonthCaption = ({ displayMonth }) => {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();

  return (
    <div className={cx('single-month')}>
      <button
        className={cx('arrow')}
        disabled={!previousMonth}
        onClick={() => previousMonth && goToMonth(previousMonth)}
        type='button'>
        <IconLeft />
      </button>
      <h2 className='caption'>{dayjs(displayMonth).format('MMMM YYYY')}</h2>
      <button
        className={cx('arrow')}
        disabled={!nextMonth}
        onClick={() => nextMonth && goToMonth(nextMonth)}
        type='button'>
        <IconRight />
      </button>
    </div>
  );
};

//TODO: support ranges
const RHFDatePickerV2 = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
  props: Omit<PropsAlt, 'onChange'> & Omit<ControllerProps<TFieldValues, TName>, 'render'>,
) => {
  return (
    <Controller
      control={props.control}
      name={props.name}
      rules={props.rules}
      render={({ field: { onChange, value, name } }) => (
        <DatePickerV2
          {...props}
          id={name}
          onChange={(v) => onChange(dayjs(v).toDate())}
          value={value ? dayjs(value).toDate() : undefined}
        />
      )}
    />
  );
};
DatePickerV2.RHF = RHFDatePickerV2;

export default DatePickerV2;
